import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

const initialState = {
  loading: false,
  error: null,
  user: localStorage.getItem("busUser")
    ? JSON.parse(localStorage.getItem("busUser"))
    : null,
  dropdownData: {},
  allCities: [],
  otpData: {},
  validateOtpLoading: false,
  dealType: {},
  otpError: null,
  countData: {},
  dealCategoriesStatus: "idle",
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (payload, { rejectWithValue }) => {
    try {
      const data = { ...payload, role: 'BUSINESS' };
      const response = await axios.post("/api/auth/login", data);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loginUserBeforeOtp = createAsyncThunk(
  "auth/loginUserBeforeOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/business/loginBeforeOtp",
        payload,
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loginUserAfterOtp = createAsyncThunk(
  "auth/loginUserAfterOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/business/loginAfterOtp", payload);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loginUserBeforeAuthenticator = createAsyncThunk(
  "auth/loginUserBeforeAuthenticator",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/business/loginBeforeAuthenticator",
        payload,
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loginUserAfterAuthenticator = createAsyncThunk(
  "auth/loginUserAfterAuthenticator",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/business/loginAfterAuthenticator",
        payload,
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const recoverPassword = createAsyncThunk(
  "auth/recoverPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/recoverPassword", {
        email,
        role: "BUSINESS",
      });
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getBusByPan = createAsyncThunk(
  "auth/getBusByPan",
  async (pan, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/business/getBusByPan?pan=${pan}`,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const sendEmailOTP = createAsyncThunk(
  "auth/sendEmailOTP",
  async (payload, { rejectWithValue }) => {
    try {
      const email = typeof payload === 'string' ? payload : payload?.email;
      const response = await axios.post(`/api/auth/forgotPassword/sendOtp`, {
        email,
        role: "BUSINESS",
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const validateEmailOTP = createAsyncThunk(
  "business/validateEmailOTP",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/auth/forgotPassword/validateOtp`,
        { ...payload, role: "BUSINESS" },
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const setNewPassword = createAsyncThunk(
  "business/setNewPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/auth/forgotPassword/reset`,
        payload,
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const signupUser = createAsyncThunk(
  "auth/signupUser",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/business/register", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getDropdownData = createAsyncThunk(
  "auth/getDropdownData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/business/getIndBusAtList");
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/logout", { role: 'BUSINESS', userId });
      // httpConfig interceptor already unwraps response.data
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getRmCities = createAsyncThunk(
  "auth/getRmCities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/business/getRmCities");
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getDealCategory = createAsyncThunk(
  "auth/getDealCategory",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("api/business/getDealCategory");
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/business/kyc/sendOtp", payload);
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const validateOtp = createAsyncThunk(
  "auth/validateOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/business/kyc/validateOtp",
        payload,
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const count = createAsyncThunk(
  "auth/count",
  async ({ type, dealCategoryId }) => {
    const response = await axios.get(
      `/api/business/count/${dealCategoryId}/${type}`,
    );
    return response.response || [];
  },
);

export const getOrgDealCategories = createAsyncThunk(
  "auth/getOrgDealCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/business/getOrgDealCategories");
      return response.response || [];
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,

  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      state.dealCategoriesStatus = "idle";
      localStorage.setItem("busUser", JSON.stringify(action.payload));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("business_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.dealCategoriesStatus = "idle";
      localStorage.removeItem("busUser");
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("business_session_active");
      }
    });
    builder.addCase(logout.rejected, (state) => {
      // Even if API fails, clear state so UI redirects to login
      state.user = null;
      state.dealCategoriesStatus = "idle";
      localStorage.removeItem("busUser");
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("business_session_active");
      }
    });

    builder.addCase(loginUserBeforeOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserBeforeOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loginUserBeforeOtp.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(loginUserAfterOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserAfterOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      state.dealCategoriesStatus = "idle";
      localStorage.setItem("busUser", JSON.stringify(action.payload));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("business_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUserAfterOtp.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(loginUserBeforeAuthenticator.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserBeforeAuthenticator.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loginUserBeforeAuthenticator.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(loginUserAfterAuthenticator.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserAfterAuthenticator.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      state.dealCategoriesStatus = "idle";
      localStorage.setItem("busUser", JSON.stringify(action.payload));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("business_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUserAfterAuthenticator.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(sendEmailOTP.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendEmailOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(sendEmailOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(recoverPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(recoverPassword.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(recoverPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(validateEmailOTP.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(validateEmailOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(validateEmailOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(setNewPassword.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(setNewPassword.fulfilled, (state, action) => {
      state.loading = false;
      state.error = "";
    });
    builder.addCase(setNewPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(signupUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(signupUser.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(signupUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(getDropdownData.fulfilled, (state, action) => {
      state.dropdownData = action.payload;
    });

    builder.addCase(getDealCategory.fulfilled, (state, action) => {
      state.dealType = action.payload;
    });

    builder.addCase(getRmCities.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getRmCities.fulfilled, (state, action) => {
      state.loading = false;
      state.allCities = action.payload;
    });

    builder.addCase(sendOtp.pending, (state) => {
      state.error = null;
    });
    builder.addCase(sendOtp.fulfilled, (state, action) => {
      state.otpData = action.payload;
      state.error = null;
    });
    builder.addCase(sendOtp.rejected, (state, action) => {
      state.otpData = {};
      state.error = action.payload;
    });

    builder.addCase(validateOtp.pending, (state) => {
      state.validateOtpLoading = true;
      state.otpError = null;
    });
    builder.addCase(validateOtp.fulfilled, (state, action) => {
      state.validateOtpLoading = false;
      state.otpError = null;
    });
    builder.addCase(validateOtp.rejected, (state, action) => {
      state.validateOtpLoading = false;
      state.otpError = action.payload;
    });

    builder.addCase(count.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(count.fulfilled, (state, action) => {
      state.loading = false;
      state.countData = action.payload;
    });
    builder.addCase(count.rejected, (state, action) => {
      state.loading = false;
      state.countData = [];
      state.error = action.error.message;
    });

    builder.addCase(getOrgDealCategories.pending, (state) => {
      state.dealCategoriesStatus = "loading";
    });
    builder.addCase(getOrgDealCategories.fulfilled, (state, action) => {
      state.dealCategoriesStatus = "succeeded";
      if (state.user && Array.isArray(action.payload)) {
        state.user.dealCategories = action.payload;
        localStorage.setItem("busUser", JSON.stringify(state.user));
      }
    });
    builder.addCase(getOrgDealCategories.rejected, (state) => {
      state.dealCategoriesStatus = "failed";
    });
  },
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
      state.otpError = null;
    },
    clearMustChangePassword: (state) => {
      if (state.user) {
        state.user.mustChangePassword = false;
        localStorage.setItem("busUser", JSON.stringify(state.user));
      }
      state.dealCategoriesStatus = "idle";
    },
  },
});

export const { clearAuthError, clearMustChangePassword } = authSlice.actions;

export default authSlice.reducer;
